import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Loader2, CheckCircle2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../context/auth";
import SEO from "../components/seo/SEO";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setBusy(true);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-4 py-20" data-testid="reset-password-page">
      <SEO title="Reset password — MyFinancesGuru Tools" noindex />
      <h1 className="font-heading text-3xl font-bold text-brand">Reset your password</h1>
      {!token ? (
        <p className="mt-4 text-sm text-red-600" data-testid="reset-no-token">This reset link is invalid or missing. Please request a new one from the sign-in screen.</p>
      ) : done ? (
        <div className="mt-6 rounded-xl border border-border bg-card p-6 text-center" data-testid="reset-success">
          <CheckCircle2 className="mx-auto h-10 w-10 text-brand" />
          <p className="mt-3 font-semibold text-foreground">Password updated!</p>
          <p className="mt-1 text-sm text-muted-foreground">You can now sign in with your new password.</p>
          <button onClick={() => navigate("/")} data-testid="reset-go-home"
            className="mt-4 rounded-lg bg-action px-5 py-2.5 text-sm font-semibold text-white hover:bg-action-hover">Go to homepage</button>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-3">
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="New password (8+ chars, incl. a number/symbol)" data-testid="reset-password-input"
              className="w-full rounded-lg border border-border bg-background py-2.5 pl-9 pr-3 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand" />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm new password" data-testid="reset-confirm-input"
              className="w-full rounded-lg border border-border bg-background py-2.5 pl-9 pr-3 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand" />
          </div>
          {error && <p className="text-sm font-medium text-red-600" data-testid="reset-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="reset-submit-btn"
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-action px-4 py-2.5 text-sm font-semibold text-white hover:bg-action-hover disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Set new password
          </button>
        </form>
      )}
    </div>
  );
}
